import type { ReportColumn, ReportOutput } from "@/profitability/types";

const columnName = (column: ReportColumn) => column.label || column.key;

const quoted = (names: string[]) => names.map((name) => `“${name}”`).join(", ");

interface ShownTable {
  id: string;
  name: string;
  /** The columns the table shows, by key, with the name the owner reads. */
  columns: Map<string, string>;
}

/**
 * The tables the report shows: its table blocks, each with the columns it
 * selects from its dataset. A dataset no table shows (one a chart reads, say)
 * is not a table to the owner, and a column a table leaves out is not shown.
 */
const tablesShown = (output: ReportOutput): Map<string, ShownTable> => {
  const datasets = new Map(output.datasets.map((set) => [set.id, set]));
  const tables = new Map<string, ShownTable>();
  for (const block of output.blocks) {
    if (block.type !== "table") continue;
    const dataset = datasets.get(block.dataset);
    const labels = new Map(
      (dataset?.columns ?? []).map((column) => [
        column.key,
        columnName(column),
      ]),
    );
    tables.set(block.id, {
      id: block.id,
      name: block.title || dataset?.title || block.id,
      columns: new Map(
        block.columns.map((key) => [key, labels.get(key) ?? key]),
      ),
    });
  }
  return tables;
};

/**
 * What publishing the draft would change in the report's shape, in the
 * owner's words: tabs and tables added or removed, columns added, removed or
 * renamed. The figures themselves are compared beside it.
 */
export const structureChanges = (
  published: ReportOutput,
  draft: ReportOutput,
): string[] => {
  const lines: string[] = [];
  const before = new Map(published.panes.map((pane) => [pane.id, pane.title]));
  const after = new Map(draft.panes.map((pane) => [pane.id, pane.title]));
  const added = draft.panes.filter((pane) => !before.has(pane.id));
  const removed = published.panes.filter((pane) => !after.has(pane.id));
  for (const pane of draft.panes) {
    const was = before.get(pane.id);
    if (was !== undefined && was !== pane.title)
      lines.push(`Tab “${was}” is now called “${pane.title}”.`);
  }
  if (added.length)
    lines.push(`New tabs: ${quoted(added.map((pane) => pane.title))}.`);
  if (removed.length)
    lines.push(
      `Tabs no longer shown: ${quoted(removed.map((pane) => pane.title))}.`,
    );

  const tablesBefore = tablesShown(published);
  const tablesAfter = tablesShown(draft);
  const newTables = [...tablesAfter.values()].filter(
    (table) => !tablesBefore.has(table.id),
  );
  const goneTables = [...tablesBefore.values()].filter(
    (table) => !tablesAfter.has(table.id),
  );
  if (newTables.length)
    lines.push(`New tables: ${quoted(newTables.map((table) => table.name))}.`);
  if (goneTables.length)
    lines.push(
      `Tables no longer shown: ${quoted(goneTables.map((table) => table.name))}.`,
    );

  for (const table of tablesAfter.values()) {
    const was = tablesBefore.get(table.id);
    if (!was) continue;
    const newColumns = [...table.columns.keys()].filter(
      (key) => !was.columns.has(key),
    );
    const goneColumns = [...was.columns.keys()].filter(
      (key) => !table.columns.has(key),
    );
    const renamed = [...table.columns].filter(
      ([key, name]) => was.columns.has(key) && was.columns.get(key) !== name,
    );
    if (newColumns.length)
      lines.push(
        `New columns in ${table.name}: ${quoted(newColumns.map((key) => table.columns.get(key)!))}.`,
      );
    if (goneColumns.length)
      lines.push(
        `Columns removed from ${table.name}: ${quoted(goneColumns.map((key) => was.columns.get(key)!))}.`,
      );
    for (const [key, name] of renamed)
      lines.push(`Column “${was.columns.get(key)!}” is now called “${name}”.`);
  }
  return lines;
};
